import { useState, useEffect, useCallback, useRef } from 'react';
import { FormulaInput } from './FormulaInput';
import { FormulaMagicWand } from './FormulaMagicWand';
import { useSimulationStore } from '../../../store/simulationStore';

interface FormulaFieldProps {
  nodeId: string;
  expression: string;
  onExpressionChange: (expression: string) => void;
}

export const FormulaField = ({ nodeId, expression, onExpressionChange }: FormulaFieldProps) => {
  const [localExpression, setLocalExpression] = useState(expression);
  const [showMagicWand, setShowMagicWand] = useState(false);
  const isFocusedRef = useRef(false); // Use ref for reliable focus tracking
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);
  const selectedNodeId = useSimulationStore((state) => state.selectedNodeId);
  const setSelection = useSimulationStore((state) => state.setSelection);
  const formulaInputRef = useRef<HTMLTextAreaElement>(null);
  const inputId = `${nodeId}-expression`;

  // Sync with external changes (e.g., undo/redo) - but only when NOT focused
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalExpression(expression);
    }
  }, [expression]);

  const handleChange = useCallback((value: string) => {
    setLocalExpression(value);
  }, []);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    registerActiveInput(inputId);
  }, [inputId, registerActiveInput]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    unregisterActiveInput(inputId);
    if (localExpression !== expression) {
      onExpressionChange(localExpression);
    }
  }, [localExpression, expression, onExpressionChange, inputId, unregisterActiveInput]);

  return (
    <>
      <label className="field" onMouseDown={(e) => {
        e.stopPropagation();
        if (selectedNodeId !== nodeId) {
          setSelection(nodeId);
        }
        formulaInputRef.current?.focus();
      }}>
        <span>Expression</span>
        <div style={{ position: 'relative' }}>
          <FormulaInput
            ref={formulaInputRef}
            value={localExpression}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            currentNodeId={nodeId}
            rows={4}
          />
          <button
            type="button"
            className="formula-magic-wand-button"
            onClick={() => setShowMagicWand(true)}
            title="Use AI to update formula"
          >
            ✨
          </button>
        </div>
      </label>
      {showMagicWand && (
        <FormulaMagicWand
          nodeId={nodeId}
          currentExpression={localExpression}
          onClose={() => setShowMagicWand(false)}
        />
      )}
    </>
  );
};

